import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Segmented, Sheet } from '@/components/common';
import type { Entry, EntryKind } from '@/types/api';
import { toothName } from '@/utils/dental';
import { EntryForm, type EntryValues } from './EntryForm';
import { ToothHistory } from './ToothHistory';

type Props = {
  tooth: number;
  entries: Entry[];
  initialTab?: 'history' | 'new';
  /** Viewing an earlier visit: history only, no new entries. */
  readOnly?: boolean;
  /** Kind for a new procedure (the chart's mode). */
  defaultKind?: EntryKind;
  onClose: () => void;
  onSave: (values: EntryValues) => Promise<void>;
};

export function ToothSheet({
  tooth,
  entries,
  initialTab,
  readOnly,
  defaultKind,
  onClose,
  onSave,
}: Props) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'history' | 'new'>(
    readOnly ? 'history' : (initialTab ?? (entries.length ? 'history' : 'new'))
  );
  const [busy, setBusy] = useState(false);

  return (
    <Sheet
      title={
        <>
          {tooth} <span className="muted small">{toothName(tooth)}</span>
        </>
      }
      onClose={onClose}
    >
      <div className="stack">
        {!readOnly && (
          <Segmented
            label={t('chart.toothSheet.view')}
            value={tab}
            onChange={setTab}
            options={[
              { value: 'history', label: t('chart.toothSheet.history', { count: entries.length }) },
              { value: 'new', label: t('chart.toothSheet.newEntry') },
            ]}
          />
        )}
        {tab === 'history' ? (
          <ToothHistory entries={entries} />
        ) : (
          <EntryForm
            key={tooth}
            initial={{ tooth }}
            defaultKind={defaultKind}
            submitLabel={t('chart.record')}
            busy={busy}
            onSubmit={async (values) => {
              setBusy(true);
              try {
                await onSave(values);
                onClose();
              } finally {
                setBusy(false);
              }
            }}
          />
        )}
      </div>
    </Sheet>
  );
}
